import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  NOTIFICATIONS_KEY,
  useInboxRefresh,
} from "@/hooks/use-notifications-refresh";
import { trackNotifications } from "@/features/notifications/analytics";
import {
  deleteNotification,
  getCategoryCounts,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
} from "@/lib/client/notifications";
import type {
  NotificationFilter,
  ReadAllInput,
} from "@/lib/autar/notifications";

const PAGE_SIZE = 20;

/**
 * The /notifications list. Pages accumulate in the query cache, so a socket
 * event or poll tick (fired by the bell) refetches what is already on screen.
 */
export function useNotificationsInbox(
  filter: NotificationFilter,
  category: string,
) {
  const refresh = useInboxRefresh();
  const categories = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, "categories"],
    queryFn: ({ signal }) => getCategoryCounts(signal),
  });
  const list = useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_KEY, "page", filter, category],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      listNotifications(
        {
          cursor: pageParam,
          limit: PAGE_SIZE,
          filter,
          category: category || undefined,
        },
        signal,
      ),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const onSettled = refresh;
  const read = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => trackNotifications("page", "mark_read"),
    onSettled,
  });
  const unread = useMutation({
    mutationFn: (id: string) => markNotificationUnread(id),
    onSettled,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteNotification(id),
    onSettled,
  });
  const readAll = useMutation({
    mutationFn: (input: ReadAllInput) => markAllNotificationsRead(input),
    onSuccess: () => trackNotifications("page", "mark_all_read"),
    onSettled,
  });

  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  return {
    items,
    categories: categories.data ?? [],
    loading: list.isPending,
    error: list.error,
    hasMore: Boolean(list.hasNextPage),
    loadingMore: list.isFetchingNextPage,
    loadMore: () => void list.fetchNextPage(),
    retry: () => void list.refetch(),
    mutationError:
      read.error ?? unread.error ?? remove.error ?? readAll.error ?? null,
    markRead: read.mutate,
    markUnread: unread.mutate,
    remove: remove.mutate,
    markAllRead: () => {
      if (items[0])
        readAll.mutate({
          upToId: items[0].id,
          category: category || undefined,
        });
    },
  };
}
